import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { WebPlaybackFacade, type PlaybackState } from "@stimulantnoise/playback";
import { BPM_VALUES, type Bpm, type ControlEvent, type LiveGenerationControls, type InstrumentPalette, type MusicGenre, type MusicMood, type PlaybackQoeEvent, type PlaybackTelemetry, type SessionSnapshot } from "@stimulantnoise/protocol";
import { initialClientSessionState, reduceSession } from "@stimulantnoise/session-core";
import { TelemetryBuffer } from "@stimulantnoise/telemetry";
import { createSession, SessionClient } from "./session-client";
import { clientId, savePendingControl } from "./storage";

type Genre = MusicGenre;

const genres: Array<{ id: Genre; label: string; note: string }> = [
  { id: "ambient", label: "drift", note: "spacious / suspended" },
  { id: "electronic", label: "current", note: "melodic / synthetic" },
  { id: "techno", label: "pulse", note: "hypnotic / precise" },
  { id: "drum-and-bass", label: "velocity", note: "breaks / sub bass" },
  { id: "cinematic", label: "arc", note: "textural / evolving" },
  { id: "jazz", label: "conversation", note: "improvised / warm" },
  { id: "post-rock", label: "horizon", note: "guitars / crescendo" }
];

function readStoredSession(): { sessionId: string; token: string; snapshot: SessionSnapshot } | undefined {
  try {
    const raw = sessionStorage.getItem("stimulantnoise.active-session");
    return raw ? JSON.parse(raw) as { sessionId: string; token: string; snapshot: SessionSnapshot } : undefined;
  } catch { return undefined; }
}

export function App() {
  const [state, dispatch] = useReducer(reduceSession, initialClientSessionState);
  const [genre, setGenre] = useState<Genre>("electronic");
  const [mood, setMood] = useState<MusicMood>("focused");
  const [palette, setPalette] = useState<InstrumentPalette>("hybrid");
  const [tempo, setTempo] = useState<Bpm>(120);
  const [draftEnergy, setDraftEnergy] = useState(0.5);
  const [session, setSession] = useState(readStoredSession);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const [starting, setStarting] = useState(false);
  const [autoStartRequested, setAutoStartRequested] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [playbackReady, setPlaybackReady] = useState(false);
  const [playbackTransitioning, setPlaybackTransitioning] = useState(false);
  const [playbackState, setPlaybackState] = useState<PlaybackState>();
  const playbackStateRef = useRef<PlaybackState | undefined>(undefined);
  const [error, setError] = useState<string>();
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent>();
  const [updateReady, setUpdateReady] = useState(false);
  const audioHost = useRef<HTMLDivElement>(null);
  const playback = useMemo(() => new WebPlaybackFacade(), []);
  const connection = useRef<SessionClient | undefined>(undefined);
  const telemetry = useMemo(() => new TelemetryBuffer(clientId()), []);
  const pendingQoeEvents = useRef<PlaybackQoeEvent[]>([]);

  useEffect(() => {
    if (!session) return;
    const controls = session.snapshot.generation_controls;
    setGenre(controls.genre);
    setMood(controls.mood);
    setPalette(controls.palette);
    setTempo(controls.tempo_bpm as Bpm);
    setDraftEnergy(session.snapshot.energy_target);
  }, [session?.sessionId]);

  useEffect(() => {
    audioHost.current?.append(playback.element);
    playback.element.className = "sr-audio";
    const unsubscribe = playback.subscribe((event) => {
      if (event.type === "state") {
        playbackStateRef.current = event.state;
        setPlaybackState(event.state);
      }
      if (event.type === "stall") {
        const activeSession = sessionRef.current;
        telemetry.emit("stall_started", activeSession ? { session_id: activeSession.sessionId } : {});
        pendingQoeEvents.current.push({ type: "stall_started", occurred_at: new Date(event.startedAt).toISOString() });
      }
      if (event.type === "stall-ended") {
        const activeSession = sessionRef.current;
        telemetry.emit("stall_ended", { ...(activeSession ? { session_id: activeSession.sessionId } : {}), fields: { duration_ms: event.durationMs } });
        pendingQoeEvents.current.push({ type: "stall_ended", occurred_at: new Date().toISOString(), duration_ms: event.durationMs });
      }
      if (event.type === "gap") {
        const activeSession = sessionRef.current;
        telemetry.emit("gap_detected", { ...(activeSession ? { session_id: activeSession.sessionId } : {}), fields: { duration_ms: event.durationMs } });
        pendingQoeEvents.current.push({ type: "gap_detected", occurred_at: new Date().toISOString(), duration_ms: event.durationMs });
      }
      if (event.type === "append-complete") {
        pendingQoeEvents.current.push({
          type: "append_complete",
          occurred_at: new Date().toISOString(),
          sequence: event.sequence,
          timeline_revision: event.timelineRevision
        });
      }
      if (event.type === "item-start") {
        pendingQoeEvents.current.push({
          type: "boundary_crossed",
          occurred_at: new Date().toISOString(),
          sequence: event.sequence
        });
      }
    });
    telemetry.emit("app_interactive");
    return () => { unsubscribe(); void playback.destroy(); };
  }, [playback, telemetry]);

  useEffect(() => {
    const onInstall = (event: Event) => { event.preventDefault(); setInstallPrompt(event as BeforeInstallPromptEvent); };
    const onUpdate = () => setUpdateReady(true);
    window.addEventListener("beforeinstallprompt", onInstall);
    window.addEventListener("stimulantnoise:update-ready", onUpdate);
    return () => {
      window.removeEventListener("beforeinstallprompt", onInstall);
      window.removeEventListener("stimulantnoise:update-ready", onUpdate);
    };
  }, []);

  useEffect(() => {
    if (!session) return;
    let active = true;
    setPlaybackReady(false);
    dispatch({ type: "snapshot", value: session.snapshot });
    void playback.initialize({ sessionId: session.sessionId, preferredAdapter: "auto", targetReserveMs: 12_000 })
      .then(() => playback.applyTimeline(session.snapshot))
      .then(() => { if (active) setPlaybackReady(true); })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? `Playback initialization failed (${reason.message}).` : "Playback initialization failed.");
      });
    const client = new SessionClient(session.sessionId, session.token, async () => {
      const current = await playback.getState();
      return {
        position_ms: current.positionMs,
        committed_buffered_ms: current.committedBufferedMs,
        total_buffered_ms: current.totalBufferedMs,
        adapter: "mse"
      };
    });
    connection.current = client;
    const unsubscribe = client.subscribe((event) => {
      if (event.type === "connection") {
        dispatch({ type: "connection", value: event.state });
        if (event.state === "connected") telemetry.emit("ws_connected", { session_id: session.sessionId });
      }
      if (event.type === "snapshot") {
        dispatch({ type: "snapshot", value: event.value });
        void playback.applyTimeline(event.value);
        sessionStorage.setItem("stimulantnoise.active-session", JSON.stringify({ ...session, snapshot: event.value }));
      }
      if (event.type === "timeline") {
        dispatch({ type: "timeline", value: event.value });
        void playback.applyTimeline(event.value).then(() => telemetry.emit("timeline_applied", { session_id: session.sessionId, timeline_revision: event.value.timeline_revision }));
      }
      if (event.type === "ack") {
        dispatch({ type: "ack", value: event.value });
        telemetry.emit("control_acknowledged", { session_id: session.sessionId, control_epoch: event.value.control_epoch });
      }
      if (event.type === "error") setError(event.message);
    });
    client.connect();
    const interval = window.setInterval(async () => {
      const current = await playback.getState();
      const events = pendingQoeEvents.current.splice(0, pendingQoeEvents.current.length);
      telemetry.drain();
      client.sendTelemetry({
        position_ms: current.positionMs,
        committed_buffered_ms: current.committedBufferedMs,
        total_buffered_ms: current.totalBufferedMs,
        adapter: "mse",
        background: document.visibilityState === "hidden",
        ...(events.length > 0 ? { events } : {})
      });
    }, 5_000);
    return () => { active = false; setPlaybackReady(false); window.clearInterval(interval); unsubscribe(); client.stop(); };
  }, [session?.sessionId, session?.token, playback, telemetry]);

  async function startSession() {
    setStarting(true);
    setAutoStartRequested(true);
    setError(undefined);
    try {
      const response = await createSession({
        palette,
        tempo_bpm: tempo,
        initial_energy: 0.5,
        idempotency_key: crypto.randomUUID(),
        client_instance_id: clientId()
      });
      const next = { sessionId: response.session_id, token: response.session_token, snapshot: response.snapshot };
      sessionStorage.setItem("stimulantnoise.active-session", JSON.stringify(next));
      setSession(next);
    } catch (reason) {
      setAutoStartRequested(false);
      setError(reason instanceof Error ? reason.message : "could not start session");
    } finally {
      setStarting(false);
    }
  }

  async function togglePlayback() {
    if (!session || !playbackReady || playbackTransitioning) return;
    const nextPlaying = !playing;
    setPlaybackTransitioning(true);
    try {
      const action = nextPlaying ? "resume" : "pause";
      // Invoke media playback inside the click task so WebKit retains user
      // activation, but do not wait for it before resuming the actor.
      const localPlay = nextPlaying
        ? playback.play().then(() => undefined, (reason: unknown) => reason instanceof Error ? reason : new Error("media playback failed"))
        : undefined;
      if (!nextPlaying) await playback.pause();
      const response = await fetch(`/v1/sessions/${session.sessionId}/${action}`, {
        method: "POST",
        headers: { authorization: `Bearer ${session.token}` }
      });
      if (!response.ok) throw new Error(`session ${action} failed (${response.status})`);
      // Resume the actor before the media element. If the local buffer ran dry
      // while paused, the actor must be allowed to commit more audio first.
      setPlaying(nextPlaying);
      const playbackError = await localPlay;
      if (playbackError) throw playbackError;
    } catch (reason) {
      setPlaying(false);
      if (nextPlaying) {
        await playback.pause().catch(() => undefined);
        await fetch(`/v1/sessions/${session.sessionId}/pause`, {
          method: "POST",
          headers: { authorization: `Bearer ${session.token}` }
        }).catch(() => undefined);
      }
      const detail = reason instanceof Error && reason.message ? ` (${reason.message})` : "";
      setError(nextPlaying
        ? `Playback could not resume${detail}. Tap play again to grant audio permission.`
        : "Playback paused locally, but the session could not confirm the pause.");
    } finally {
      setPlaybackTransitioning(false);
    }
  }

  useEffect(() => {
    if (!autoStartRequested || !session || !playbackReady || playing || playbackTransitioning) return;
    setAutoStartRequested(false);
    void togglePlayback();
  }, [autoStartRequested, session?.sessionId, playbackReady, playing, playbackTransitioning]);

  useEffect(() => {
    const snapshot = state.snapshot;
    if (!session || !snapshot || state.connection !== "connected") return;
    const current = snapshot.generation_controls;
    if (current.genre === genre && current.mood === mood && current.palette === palette
      && current.tempo_bpm === tempo && snapshot.energy_target === draftEnergy) return;
    const timer = window.setTimeout(() => {
      const controls: LiveGenerationControls = { genre, mood, palette, tempo_bpm: tempo, energy: draftEnergy };
      const event: ControlEvent = {
        event_id: crypto.randomUUID(),
        session_id: session.sessionId,
        kind: "generation_controls",
        controls,
        transition: "phrase_morph",
        client_time: new Date().toISOString(),
        base_control_epoch: snapshot.control_epoch,
        ...(playbackStateRef.current ? { playback: {
          position_ms: playbackStateRef.current.positionMs,
          committed_buffered_ms: playbackStateRef.current.committedBufferedMs,
          total_buffered_ms: playbackStateRef.current.totalBufferedMs
        } } : {})
      };
      void savePendingControl(event).then(() => {
        dispatch({ type: "control.pending", event_id: event.event_id, value: controls, submitted_at: event.client_time });
        telemetry.emit("control_submitted", { session_id: session.sessionId });
        connection.current?.sendControl(event);
      });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [session?.sessionId, state.snapshot?.control_epoch, state.snapshot?.generation_controls, state.snapshot?.energy_target, state.connection, genre, mood, palette, tempo, draftEnergy, telemetry]);

  async function endSession() {
    if (session) {
      await fetch(`/v1/sessions/${session.sessionId}/end`, {
        method: "POST",
        headers: { authorization: `Bearer ${session.token}` }
      }).catch(() => undefined);
    }
    connection.current?.stop();
    sessionStorage.removeItem("stimulantnoise.active-session");
    setSession(undefined);
    setPlaying(false);
    dispatch({ type: "connection", value: "offline" });
    location.reload();
  }

  const energy = draftEnergy;
  const pendingCount = Object.keys(state.pending_controls).length;
  const effectiveInSeconds = state.snapshot?.controls_effective_from_ms !== undefined
    ? Math.max(0, Math.ceil((state.snapshot.controls_effective_from_ms - (playbackState?.positionMs ?? 0)) / 1000))
    : undefined;

  return (
    <main className="shell">
      <div className="grain" aria-hidden="true" />
      <header className="masthead">
        <a className="brand" href="/" aria-label="stimulantnoise generator home">
          <span>stimulant</span><span className="brand-accent">noise</span><span className="brand-slash">/gen</span>
        </a>
        <div className="header-actions">
          {installPrompt && <button className="text-button" onClick={() => void installPrompt.prompt()}>install</button>}
          <span className={`connection connection--${state.connection}`}><i />{state.connection}</span>
        </div>
      </header>

      {!session ? (
        <section className="landing">
          <div className="landing-copy">
            <p className="eyebrow">continuous composition / responsive matter</p>
            <h1>Sound that<br />moves <em>with you.</em></h1>
            <p className="lede">Start a live piece, then lean into it or pull it back. The near future rewrites itself without breaking the flow.</p>
          </div>
          <div className="start-panel">
            <p className="panel-label">choose your opening</p>
            <div className="parameter-grid">
              <label><span>palette</span><select value={palette} onChange={(event) => setPalette(event.target.value as InstrumentPalette)}>
                <option value="electronic">electronic</option><option value="acoustic">acoustic</option><option value="hybrid">hybrid</option>
              </select></label>
              <label><span>bpm</span><select aria-label="bpm" value={tempo} onChange={(event) => setTempo(Number(event.target.value) as Bpm)}>
                {BPM_VALUES.map((bpm) => <option key={bpm} value={bpm}>{bpm} bpm</option>)}
              </select></label>
            </div>
            <button className="start-button" onClick={() => void startSession()} disabled={starting}>
              <span>{starting ? "assembling…" : "enter the signal"}</span><b aria-hidden="true">↗</b>
            </button>
            <p className="microcopy">A benchmarked generated opening starts immediately. Shape the next phrase once it is playing.</p>
          </div>
        </section>
      ) : (
        <section className="session-stage">
          <div className="session-meta">
            <span>session {session.sessionId.slice(0, 8)}</span>
            <span>rev {state.last_revision}</span>
            <span>{state.snapshot?.active_provider_profile}</span>
            {state.snapshot?.audible_generation_controls && <span>audible {state.snapshot.audible_generation_controls.genre} / {state.snapshot.audible_generation_controls.mood} / {state.snapshot.audible_generation_controls.tempo_bpm} bpm / {Math.round(state.snapshot.audible_generation_controls.energy * 100)} energy</span>}
          </div>
          <div className={playing ? "signal active" : "signal"} style={{ "--energy": energy } as React.CSSProperties}>
            <div className="signal-ring ring-a" /><div className="signal-ring ring-b" /><div className="signal-ring ring-c" />
            <button className="play-core" aria-label={playing ? "Pause" : "Play"} aria-busy={!playbackReady || playbackTransitioning} onClick={() => void togglePlayback()} disabled={!playbackReady || playbackTransitioning}>
              <span>{playing ? "Ⅱ" : "▶"}</span>
            </button>
            <div className="energy-readout"><strong>{Math.round(energy * 100)}</strong><span>energy</span></div>
          </div>
          <div className="live-controls">
            <div className="live-controls-heading">
              <span>shape the next phrase</span>
              <small>{state.snapshot?.pending_generation_controls
                ? `queued${state.snapshot.controls_effective_from_sequence !== undefined ? ` · phrase ${state.snapshot.controls_effective_from_sequence}` : ""}${effectiveInSeconds !== undefined ? ` · audible in ~${effectiveInSeconds}s` : ""}`
                : pendingCount ? "sending intent" : "future aligned"}</small>
            </div>
            <div className="live-control-grid">
              <label><span>genre</span><select aria-label="live genre" value={genre} onChange={(event) => setGenre(event.target.value as Genre)}>
                {genres.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
              </select></label>
              <label><span>mood</span><select aria-label="live mood" value={mood} onChange={(event) => setMood(event.target.value as MusicMood)}>
                <option value="calm">calm</option><option value="focused">focused</option><option value="euphoric">euphoric</option><option value="dark">dark</option><option value="dreamy">dreamy</option>
              </select></label>
              <label><span>palette</span><select aria-label="live palette" value={palette} onChange={(event) => setPalette(event.target.value as InstrumentPalette)}>
                <option value="electronic">electronic</option><option value="acoustic">acoustic</option><option value="hybrid">hybrid</option>
              </select></label>
              <label><span>bpm</span><select aria-label="live tempo" value={tempo} onChange={(event) => setTempo(Number(event.target.value) as Bpm)}>
                {BPM_VALUES.map((bpm) => <option key={bpm} value={bpm}>{bpm} bpm</option>)}
              </select></label>
              <label className="energy-control"><span>energy <output>{Math.round(draftEnergy * 100)}</output></span><input aria-label="live energy" type="range" min="0" max="100" step="1" value={Math.round(draftEnergy * 100)} onChange={(event) => setDraftEnergy(Number(event.target.value) / 100)} />
                <small><i>minimal</i><i>restrained</i><i>steady</i><i>driving</i><i>maximal</i></small>
              </label>
            </div>
            <div className="deck-status">
              <span>{playbackState?.status ?? "ready"}</span>
              <div className="reserve"><i style={{ width: `${Math.min(100, (playbackState?.committedBufferedMs ?? 0) / 120)}%` }} /></div>
              <small>{state.snapshot?.degraded?.active ? state.snapshot.degraded.reason : state.snapshot?.generation_status?.phase === "waiting" ? "waiting for the next phrase" : state.snapshot?.generation_status?.phase === "generating" || state.snapshot?.generation_status?.phase === "warming" ? "preparing the next phrase" : "one phrase at a time"}</small>
            </div>
          </div>
          {(state.snapshot?.generation_status?.phase === "waiting") && <div className="notice waiting" role="status"><span className="spinner" />waiting for the next phrase</div>}
          <button className="end-button" onClick={() => void endSession()}>end session</button>
        </section>
      )}

      {error && <div className="notice error" role="alert">{error}<button onClick={() => setError(undefined)}>dismiss</button></div>}
      {state.conflict && <div className="notice error" role="alert">Timeline resync required. Playback is holding committed audio.</div>}
      {updateReady && <div className="notice update">A new release is ready.<button onClick={() => window.dispatchEvent(new Event("stimulantnoise:apply-update"))}>refresh safely</button></div>}
      <div ref={audioHost} />
    </main>
  );
}
